import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { DatesSetArg, EventClickArg, EventContentArg, EventDropArg, EventHoveringArg } from "@fullcalendar/core";
import { getCourseCategoryLabel } from "@eagerdog/constants";
import moment from "moment";

import ReactDOM from "react-dom";
import { apiService } from "src/services";
import { useClubCalendarData, ClubCalendarEvent } from "src/hooks/useClubCalendarData";
import Button from "src/components/Button/Button";
import SendMessage, { SendMessageType } from "src/components/SendMessage/SendMessage";
import { IEventMessage } from "@eagerdog/interfaces";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./ClubCalendar.module.scss";

interface IProps {
  clubId: string;
  isAdmin?: boolean;
  showEvents?: boolean;
  showCourses?: boolean;
  activeFilters?: Set<string>;
  /** When provided, skips the club data fetch and renders these events directly. */
  initialEvents?: ClubCalendarEvent[];
  /** Called after a drag-and-drop reschedule succeeds when operating in initialEvents mode. */
  onEventsChanged?: (events: ClubCalendarEvent[]) => void;
}

interface IRescheduleConfirm {
  id: string;
  type: "event" | "course";
  title: string;
  oldStart: Date;
  oldEnd: Date;
  newStart: Date;
  newEnd: Date;
  deltaDays: number;
  revert: () => void;
  /** 0-based index of the dragged session (courses only). undefined = move all. */
  fromSessionIndex?: number;
  /** 1-based display index (courses only) */
  sessionIndex?: number;
  sessionTotal?: number;
}

type ViewType = "dayGridMonth" | "timeGridWeek" | "timeGridDay" | "multiMonthYear";

// ─── Tooltip ──────────────────────────────────────────────────────────────────
// Add any new fields here and render them in EventTooltip below.
interface ITooltipData {
  x: number;
  y: number;
  type: "event" | "course";
  title: string;
  status?: string;
  showTypes?: string[];
  sessionIndex?: number;
  sessionTotal?: number;
  instructor?: string;
  enrolled?: number;
  capacity?: number;
  clubName?: string;
  city?: string;
  state?: string;
  country?: string;
}

const STATUS_LABELS: Record<string, string> = {
  published: "Live",
  upcoming:  "Upcoming",
  finished:  "Finished",
  draft:     "Draft",
};

const TOOLTIP_OFFSET_X = 14;
const TOOLTIP_OFFSET_Y = 10;
const TOOLTIP_WIDTH    = 240;
const TOOLTIP_HEIGHT   = 120; // approximate — used for edge detection only

const EventTooltip: React.FC<{ data: ITooltipData }> = ({ data }) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const left = data.x + TOOLTIP_OFFSET_X + TOOLTIP_WIDTH > vw
    ? data.x - TOOLTIP_OFFSET_X - TOOLTIP_WIDTH
    : data.x + TOOLTIP_OFFSET_X;

  const top = data.y + TOOLTIP_OFFSET_Y + TOOLTIP_HEIGHT > vh
    ? data.y - TOOLTIP_OFFSET_Y - TOOLTIP_HEIGHT
    : data.y + TOOLTIP_OFFSET_Y;

  const statusLabel = data.status ? (STATUS_LABELS[data.status] ?? data.status) : null;
  const uniqueShowTypes = data.showTypes ? [...new Set(data.showTypes)] : [];

  return (
    <div
      className={styles.tooltip}
      style={{ left, top }}
      // Rows to add/change: edit JSX below.
    >
      <div className={styles.tooltipHeader}>
        <div className={`${styles.tooltipType} ${data.type === "event" ? styles.tooltipTypeEvent : styles.tooltipTypeCourse}`}>
          {data.type === "event" ? "Event" : "Course"}
        </div>
        {statusLabel && (
          <div className={`${styles.tooltipStatus} ${data.status === "published" ? styles.tooltipStatusLive : ""}`}>
            {statusLabel}
          </div>
        )}
      </div>
      <div className={styles.tooltipTitle}>{data.title}</div>
      {data.sessionIndex != null && data.sessionTotal != null && (
        <div className={styles.tooltipSession}>
          Session {data.sessionIndex} of {data.sessionTotal}
        </div>
      )}
      {data.instructor && (
        <div className={styles.tooltipRow}>Instructor: {data.instructor}</div>
      )}
      {data.capacity != null && data.capacity > 0 && (
        <div className={styles.tooltipRow}>
          Enrolled: {data.enrolled ?? 0}/{data.capacity}
        </div>
      )}
      {uniqueShowTypes.length > 0 && (
        <div className={styles.tooltipTags}>
          {uniqueShowTypes.map((t) => (
            <span key={t} className={styles.tooltipTag}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Lightweight dialog that doesn't lock/restore body scroll (avoids page jump on close)
const CalendarDialog: React.FC<{ isShown: boolean; onClose: () => void; children: React.ReactNode; cardClassName?: string }> = ({
  isShown,
  onClose,
  children,
  cardClassName,
}) => {
  if (!isShown) return null;
  return ReactDOM.createPortal(
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div className={`${styles.dialogCard} ${cardClassName || ""}`} onClick={(e) => e.stopPropagation()}>
        <button className={styles.dialogClose} onClick={onClose} type="button" aria-label="Close">✕</button>
        {children}
      </div>
    </div>,
    document.body
  );
};

const VIEW_OPTIONS: { label: string; value: ViewType }[] = [
  { label: "Day",   value: "timeGridDay" },
  { label: "Week",  value: "timeGridWeek" },
  { label: "Month", value: "dayGridMonth" },
  { label: "Year",  value: "multiMonthYear" },
];

const ClubCalendar: React.FC<IProps> = ({
  clubId,
  isAdmin = false,
  showEvents = true,
  showCourses = true,
  activeFilters = new Set(),
  initialEvents,
  onEventsChanged,
}) => {
  const navigate = useNavigate();
  const calendarRef = useRef<FullCalendar>(null);

  const [activeView, setActiveView] = useState<ViewType>("dayGridMonth");
  const [title, setTitle] = useState("");

  const [rescheduleConfirm, setRescheduleConfirm] = useState<IRescheduleConfirm | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showNotifyPrompt, setShowNotifyPrompt] = useState(false);
  const [pendingNotify, setPendingNotify] = useState<{
    id: string;
    type: "event" | "course";
    title: string;
    deltaDays: number;
    fromSessionIndex?: number;
    toSessionIndex?: number;
    oldStart?: Date;
    oldEnd?: Date;
    newStart?: Date;
    newEnd?: Date;
  } | null>(null);
  const [isSendingNotify, setIsSendingNotify] = useState(false);
  // Controls whether to move only the dragged session or all remaining ones (default: single session only)
  const [moveRemaining, setMoveRemaining] = useState(false);

  const [tooltip, setTooltip] = useState<ITooltipData | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { events: fetchedEvents, loading, fetchRange, shiftCourseSessionDates } = useClubCalendarData({
    clubId: initialEvents ? "" : clubId,
    isAdmin,
  });

  const events = initialEvents ?? fetchedEvents;

  const filteredEvents = useMemo(() => {
    if (initialEvents) return initialEvents;
    return fetchedEvents.filter((e: ClubCalendarEvent) => {
      const isEvent = e.extendedProps.type === "event";
      if (isEvent && !showEvents) return false;
      if (!isEvent && !showCourses) return false;
      if (activeFilters.size === 0) return true;
      if (isEvent) {
        return (e.extendedProps.showTypes || []).some((t: string) => activeFilters.has(t));
      }
      return activeFilters.has(e.extendedProps.category || "");
    });
  }, [events, showEvents, showCourses, activeFilters, initialEvents, fetchedEvents]);

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setTitle(arg.view.title);
      fetchRange(arg.start, arg.end);
    },
    [fetchRange]
  );

  const handleEventClick = useCallback(
    (arg: EventClickArg) => {
      if (isSaving) return;
      const { type, handle } = arg.event.extendedProps;
      navigate(type === "event" ? `/e/${handle}` : `/c/${handle}`);
    },
    [navigate, isSaving]
  );

  const handleMouseEnter = useCallback((arg: EventHoveringArg) => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    tooltipTimerRef.current = setTimeout(() => {
      const { type, clubName, city, state, country, sessionIndex, sessionTotal, instructor, enrolled, capacity, showTypes, status } = arg.event.extendedProps;
      const jsEvent = arg.jsEvent as MouseEvent;
      setTooltip({
        x: jsEvent.clientX,
        y: jsEvent.clientY,
        type,
        title: arg.event.title,
        status,
        showTypes,
        sessionIndex,
        sessionTotal,
        instructor,
        enrolled,
        capacity,
        clubName,
        city,
        state,
        country,
      });
    }, 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    setTooltip(null);
  }, []);

  const handleViewChange = (view: ViewType) => {
    setActiveView(view);
    calendarRef.current?.getApi().changeView(view);
  };

  const handlePrev  = () => calendarRef.current?.getApi().prev();
  const handleNext  = () => calendarRef.current?.getApi().next();
  const handleToday = () => calendarRef.current?.getApi().today();

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api) setTitle(api.view.title);
  }, []);

  // ---- Drag & Drop ----
  const handleEventDrop = useCallback(
    (arg: EventDropArg) => {
      const { delta, event, revert } = arg;
      const deltaDays = Math.round(
        delta.milliseconds / (24 * 60 * 60 * 1000) + delta.days + (delta.months * 30)
      );

      if (deltaDays === 0) {
        revert();
        return;
      }

      const { type, sessionIndex, sessionTotal } = event.extendedProps;
      // For course sessions, use the stored sourceId; for events strip the prefix
      const rawId = type === "course"
        ? (event.extendedProps.sourceId || event.id.replace(/^course-/, "").split("-s")[0])
        : event.id.replace(/^event-/, "");

      const oldStart = arg.oldEvent.start!;
      const oldEnd   = arg.oldEvent.end ?? oldStart;
      const newStart = event.start!;
      const newEnd   = event.end ?? newStart;

      // For course sessions: the dragged session and all after it move; earlier ones stay.
      // sessionIndex is 1-based → convert to 0-based for the API.
      const fromSessionIndex = type === "course" && sessionIndex != null
        ? sessionIndex - 1
        : undefined;

      setSaveError(null);
      setRescheduleConfirm({
        id: rawId,
        type,
        title: event.title,
        oldStart,
        oldEnd,
        newStart,
        newEnd,
        deltaDays,
        revert,
        fromSessionIndex,
        sessionIndex,
        sessionTotal,
      });
    },
    []
  );

  const handleConfirmReschedule = useCallback(
    async () => {
      if (!rescheduleConfirm) return;
      setIsSaving(true);
      setSaveError(null);

      try {
        // For course sessions: if moveRemaining is off, cap to just the dragged session
        const toSessionIndex =
          rescheduleConfirm.type === "course" &&
          rescheduleConfirm.fromSessionIndex !== undefined &&
          !moveRemaining
            ? rescheduleConfirm.fromSessionIndex + 1
            : undefined;

        if (rescheduleConfirm.type === "event") {
          await apiService.rescheduleEvent(rescheduleConfirm.id, rescheduleConfirm.deltaDays, false);
        } else {
          await apiService.rescheduleCourse(
            rescheduleConfirm.id,
            rescheduleConfirm.deltaDays,
            false,
            rescheduleConfirm.fromSessionIndex,
            toSessionIndex
          );

          const draggedIdx = rescheduleConfirm.fromSessionIndex ?? 0;
          const deltaMs = rescheduleConfirm.deltaDays * 24 * 60 * 60 * 1000;

          if (initialEvents && onEventsChanged) {
            // In initialEvents mode (e.g. Course Schedule tab), propagate the shift back
            // to the parent so the events prop stays consistent. We shift ALL affected
            // sessions (including the dragged one) because FullCalendar will re-render
            // from the updated initialEvents on the next render cycle.
            const updatedEvents = initialEvents.map(event => {
              if (event.extendedProps.type !== "course") return event;
              if (event.extendedProps.sourceId !== rescheduleConfirm.id) return event;
              const evtIdx = (event.extendedProps.sessionIndex ?? 1) - 1;
              if (evtIdx < draggedIdx) return event;
              if (toSessionIndex !== undefined && evtIdx >= toSessionIndex) return event;
              return {
                ...event,
                start: new Date(new Date(event.start as string).getTime() + deltaMs).toISOString(),
                end:   new Date(new Date((event.end ?? event.start) as string).getTime() + deltaMs).toISOString(),
              };
            });
            onEventsChanged(updatedEvents);
          } else {
            // Normal club-calendar mode: update internal fetched-events state.
            // Skip the dragged session (FullCalendar already moved it during the drag).
            const shiftFrom = draggedIdx + 1;
            shiftCourseSessionDates(rescheduleConfirm.id, shiftFrom, toSessionIndex, deltaMs);
          }
        }

        // Refresh React state to stay in sync (only meaningful in club-calendar mode)
        if (!initialEvents) {
          const api = calendarRef.current?.getApi();
          if (api) {
            fetchRange(api.view.activeStart, api.view.activeEnd);
          }
        }

        // Show the "notify registrants?" follow-up
        setPendingNotify({
          id: rescheduleConfirm.id,
          type: rescheduleConfirm.type,
          title: rescheduleConfirm.title,
          deltaDays: rescheduleConfirm.deltaDays,
          fromSessionIndex: rescheduleConfirm.fromSessionIndex,
          toSessionIndex,
          oldStart: rescheduleConfirm.oldStart,
          oldEnd: rescheduleConfirm.oldEnd,
          newStart: rescheduleConfirm.newStart,
          newEnd: rescheduleConfirm.newEnd,
        });
        setMoveRemaining(false);
        setRescheduleConfirm(null);
        setShowNotifyPrompt(true);
      } catch (err: any) {
        setSaveError(err?.response?.data?.message || "Failed to reschedule. Please try again.");
        rescheduleConfirm.revert();
        setRescheduleConfirm(null);
      } finally {
        setIsSaving(false);
      }
    },
    [rescheduleConfirm, fetchRange, moveRemaining, shiftCourseSessionDates, initialEvents, onEventsChanged]
  );

  const handleCancelReschedule = useCallback(() => {
    rescheduleConfirm?.revert();
    setRescheduleConfirm(null);
    setSaveError(null);
    setMoveRemaining(false);
  }, [rescheduleConfirm]);

  const handleSendNotifyMessage = useCallback(async (formData: IEventMessage) => {
    if (!pendingNotify) return;
    setIsSendingNotify(true);
    try {
      if (pendingNotify.type === "event") {
        await apiService.requestRescheduleNotificationForEvent(
          pendingNotify.id,
          formData,
          pendingNotify.oldStart?.toISOString(),
          pendingNotify.oldEnd?.toISOString(),
          pendingNotify.newStart?.toISOString(),
          pendingNotify.newEnd?.toISOString(),
        );
      } else {
        await apiService.requestSendMessageEmailForCourse(
          pendingNotify.id,
          formData,
          pendingNotify.oldStart?.toISOString(),
          pendingNotify.oldEnd?.toISOString(),
          pendingNotify.newStart?.toISOString(),
          pendingNotify.newEnd?.toISOString(),
        );
      }
      toast.show({ title: "Notification sent", content: "Registrants have been notified.", duration: 5000, type: "success" });
      setShowNotifyPrompt(false);
      setPendingNotify(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to send notification. Please try again.";
      toast.show({ title: "Send failed", content: msg, duration: 8000, type: "fail" });
    } finally {
      setIsSendingNotify(false);
    }
  }, [pendingNotify]);

  const handleNotifyNo = useCallback(() => {
    setShowNotifyPrompt(false);
    setPendingNotify(null);
  }, []);

  const renderEventContent = (arg: EventContentArg) => {
    const { type, showTypes, category, sessionIndex, sessionTotal } = arg.event.extendedProps;
    const isEvent = type === "event";

    let subtitle: string;
    if (!isEvent && sessionIndex != null && sessionTotal != null) {
      subtitle = `Session ${sessionIndex} of ${sessionTotal}`;
    } else if (isEvent) {
      subtitle = (showTypes || []).slice(0, 2).join(" · ");
    } else {
      subtitle = category ? getCourseCategoryLabel(category) : "Course";
    }

    return (
      <div className={`${styles.tile} ${isEvent ? styles.tileEvent : styles.tileCourse}`}>
        <span className={styles.dot} />
        <div className={styles.tileContent}>
          <span className={styles.tileTitle}>{arg.event.title}</span>
          {subtitle && <span className={styles.tileSub}>{subtitle}</span>}
        </div>
      </div>
    );
  };

  const formatDateRange = (start: Date, end: Date) => {
    const s = moment(start).format("MMM D");
    const e = moment(end).format("MMM D, YYYY");
    return s === e ? s : `${s} – ${e}`;
  };

  return (
    <div className={styles.ClubCalendar}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <button className={styles.todayBtn} onClick={handleToday}>Today</button>
          <div className={styles.navBtns}>
            <button className={styles.navBtn} onClick={handlePrev} aria-label="Previous">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className={styles.navBtn} onClick={handleNext} aria-label="Next">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <span className={styles.calTitle}>{title}</span>
        </div>

        <div className={styles.toolbarRight}>
          {loading && <span className={styles.loadingDot} />}
          <div className={styles.viewToggle}>
            {VIEW_OPTIONS.map((v) => (
              <button
                key={v.value}
                className={`${styles.viewBtn} ${activeView === v.value ? styles.viewBtnActive : ""}`}
                onClick={() => handleViewChange(v.value)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error toast */}
      {saveError && (
        <div className={styles.errorBanner}>
          {saveError}
          <button className={styles.errorClose} onClick={() => setSaveError(null)}>✕</button>
        </div>
      )}

      {/* Calendar grid */}
      <div className={styles.calendarWrap}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, multiMonthPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          events={filteredEvents}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          eventMouseEnter={handleMouseEnter}
          eventMouseLeave={handleMouseLeave}
          height="auto"
          firstDay={0}
          multiMonthMaxColumns={3}
          dayMaxEvents={3}
          eventDisplay="block"
          nowIndicator={true}
          fixedWeekCount={false}
          editable={isAdmin && activeView !== "multiMonthYear"}
          droppable={isAdmin && activeView !== "multiMonthYear"}
          eventDrop={isAdmin ? handleEventDrop : undefined}
          snapDuration={{ days: 1 }}
          dragScroll={true}
        />
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotEvent}`} />
          <span>Events</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotCourse}`} />
          <span>Courses</span>
        </div>
        {isAdmin && activeView !== "multiMonthYear" && (
          <div className={styles.legendItem}>
            <span className={styles.legendDrag}>Drag to reschedule</span>
          </div>
        )}
      </div>

      {/* Reschedule confirmation dialog */}
      <CalendarDialog isShown={!!rescheduleConfirm} onClose={handleCancelReschedule}>
        {rescheduleConfirm && (
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Reschedule {rescheduleConfirm.type === "event" ? "Event" : "Course"}?</h3>
            <p className={styles.modalBody}>
              <strong>{rescheduleConfirm.title}</strong>
            </p>
            <div className={styles.modalDates}>
              <span className={styles.dateLine}>
                <span className={styles.dateLabel}>From</span>
                {formatDateRange(rescheduleConfirm.oldStart, rescheduleConfirm.oldEnd)}
              </span>
              <span className={styles.dateArrow}>→</span>
              <span className={styles.dateLine}>
                <span className={styles.dateLabel}>To</span>
                {formatDateRange(rescheduleConfirm.newStart, rescheduleConfirm.newEnd)}
              </span>
            </div>
            {rescheduleConfirm.type === "event" ? (
              <p className={styles.modalNote}>All shows will be moved by the same number of days.</p>
            ) : rescheduleConfirm.sessionIndex != null &&
                rescheduleConfirm.sessionTotal != null &&
                rescheduleConfirm.sessionIndex < rescheduleConfirm.sessionTotal ? (
              <label className={styles.moveRemainingLabel}>
                <input
                  type="checkbox"
                  checked={moveRemaining}
                  onChange={(e) => setMoveRemaining(e.target.checked)}
                />
                <span>
                  {moveRemaining
                    ? `Move sessions ${rescheduleConfirm.sessionIndex} – ${rescheduleConfirm.sessionTotal}`
                    : "Move remaining sessions"}
                </span>
              </label>
            ) : null}
            {saveError && <p className={styles.modalError}>{saveError}</p>}
            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={handleCancelReschedule} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleConfirmReschedule} disabled={isSaving} isLoading={isSaving}>
                Confirm
              </Button>
            </div>
          </div>
        )}
      </CalendarDialog>

      {/* Notify registrants — compose dialog */}
      <CalendarDialog isShown={showNotifyPrompt} onClose={handleNotifyNo} cardClassName={styles.dialogCardWide}>
        {pendingNotify && (
          <div className={styles.notifyCompose}>
            <SendMessage
              type={SendMessageType.CourseRegistrants}
              subject={`${pendingNotify.type === "course" ? "Course" : "Event"} Rescheduled: ${pendingNotify.title}`}
              messageBody={`<p>The dates for <strong>${pendingNotify.title}</strong> have been updated. Please review the new schedule and contact us if you have any questions.</p>`}
              handleSendMessage={handleSendNotifyMessage}
              isSending={isSendingNotify}
              title="Notify Registrants"
            />
            <div className={styles.notifySkip}>
              <button type="button" className={styles.notifySkipBtn} onClick={handleNotifyNo}>
                Skip — don't notify registrants
              </button>
            </div>
          </div>
        )}
      </CalendarDialog>

      {/* Hover tooltip */}
      {tooltip && <EventTooltip data={tooltip} />}
    </div>
  );
};

export default ClubCalendar;
